import type { TEnvironment } from "@/core/environment/types";
import type { TAccount } from "@/shared/services";
import { environment } from "@environment";
import moment from "moment";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

type TSeats = { planLimit: number; additionalSeats: number; freeSeats: number };

type AccountState = {
  _id: string | null;
  isPasswordEnabled: boolean;
  isPoweredByStorydocDisabled: boolean;
  isSubscribed: boolean;
  isCancelled: boolean;
  isTrialEnded: boolean;
  isPaused: boolean;
  isSelfServePaying: boolean;
  isPaymentPending: boolean;
  isAppSumo: boolean;
  cancelAt: Date | undefined;
  invitationToken: string | undefined;
  planType: "trial" | "starter" | "pro" | "team";
  subscription: TEnvironment["paddle"]["plans"][number] | null;
  isSubscriptionExpired: boolean;
  seats: TSeats;
  account: TAccount | null;
  showAnalyticsPlaceholder: boolean;
  isScrollBySlideEnabled: boolean;
};

interface AccountActions {
  setAccount: (account: TAccount) => void;
  reset: () => void;
}

const initialState: AccountState = {
  _id: null,
  isPasswordEnabled: false,
  isPoweredByStorydocDisabled: false,
  isSubscribed: false,
  isCancelled: false,
  isTrialEnded: false,
  isPaused: false,
  isSelfServePaying: false,
  isPaymentPending: false,
  isAppSumo: false,
  cancelAt: undefined,
  invitationToken: undefined,
  planType: "trial",
  subscription: null,
  isSubscriptionExpired: false,
  seats: { planLimit: 0, additionalSeats: 0, freeSeats: 0 },
  account: null,
  showAnalyticsPlaceholder: false,
  isScrollBySlideEnabled: false,
};

export const useAccountStore = create<AccountState & AccountActions>((set) => ({
  ...initialState,
  setAccount: (account) => {
    const isSubscribed = Boolean(
      account.subscriptionId && account.subscriptionId !== null,
    );
    const cancelAt = account.cancelAt;
    const subscription =
      environment.paddle.plans.find(
        (plan) => plan.id === account.subscriptionPlanId,
      ) || null;

    const showAnalyticsPlaceholder =
      account.showAnalyticsPlaceholder ||
      (!account?.firstPublish &&
        !account?.firstVersionCreated &&
        !account?.analyticsViewId);

    const planType = getPlanType(account, subscription);

    set({
      _id: account._id,
      account,
      isPasswordEnabled: Boolean(account.passwordEnabled),
      isPoweredByStorydocDisabled: Boolean(account.hidePoweredByStorydoc),
      isSubscribed,
      isCancelled: Boolean(cancelAt),
      isTrialEnded: isTrialEndedFn(account),
      isPaused: Boolean(account.subscriptionStatus === "paused"),
      cancelAt,
      isSelfServePaying: Boolean(
        isSubscribed && account.isSelfServ && !cancelAt,
      ),
      invitationToken: account.invitationToken,
      isPaymentPending: Boolean(account.paymentPending),
      isAppSumo: Boolean(account.appsumoUuid),
      planType,
      subscription,
      seats: getAccountSeats(account, subscription),
      isSubscriptionExpired: isSubscriptionExpired(account.trialEnd),
      showAnalyticsPlaceholder,
      isScrollBySlideEnabled: Boolean(account.isScrollBySlideEnabled),
    });
  },
  reset: () => set(initialState),
}));

export const useAccountStoreShallow = <U>(
  selector: (store: AccountState & AccountActions) => U,
) => useAccountStore(useShallow(selector));

// ===============================
const isSubscriptionExpired = (trialEnd: Date | undefined) => {
  if (!trialEnd) return false;
  const now = moment(new Date());
  const end = moment(trialEnd);
  const duration = moment.duration(end.diff(now));
  const days = duration.asDays();
  return days < 0;
};

const isPaidFn = (account: TAccount | null) => {
  if (!account) return false;
  return account.plan === "paid" || account.plan === undefined;
};

const getPlanType = (
  account: TAccount | null,
  subscription: AccountState["subscription"],
): AccountState["planType"] => {
  if (!account) return "trial";
  const paid = isPaidFn(account);
  if (paid && !account.isSelfServ) return "team";
  if (!subscription) return "trial";
  return subscription.title?.toLowerCase() as AccountState["planType"];
};

const getAccountSeats = (
  account: TAccount | null,
  subscription: AccountState["subscription"],
): { planLimit: number; additionalSeats: number; freeSeats: number } => {
  if (!account) return { planLimit: 0, additionalSeats: 0, freeSeats: 0 };
  const planLimit = subscription?.seats ? subscription.seats : 1;
  const additionalSeats = account.additionalSeats
    ? Number.parseInt(account.additionalSeats)
    : 0;
  const freeSeats = account.planProperties
    ? account.planProperties.freeSeats || 0
    : 0;
  return { planLimit, additionalSeats, freeSeats };
};

const isTrialEndedFn = (account: TAccount | null) => {
  if (!account || !account.trialEnd || isPaidFn(account)) return false;
  const now = moment(new Date());
  const end = moment(account.trialEnd);
  const duration = moment.duration(end.diff(now));
  const days = duration.asDays();
  return days < 0;
};
// ===============================
